/**
 * A drill opened from inside a module, played the same way it plays anywhere
 * else: one big layer over the page.
 *
 * It used to swap the card in place for a small inline iframe, which was
 * wrong twice. A clip of five players working on a finish is unreadable at
 * card width, and the inline player was a second way of watching a drill,
 * with none of what the real one does.
 *
 * What the real one does matters more than the size. `VideoTheater` reports
 * the halfway point, and that is what writes the `drill_views` row behind the
 * dimming. Without it, watching a drill inside a module never marked it
 * watched: the tabs read that state but nothing in them ever wrote it, so a
 * card only came back dimmed if the coach had happened to watch it over in
 * Drills and Games. Reusing the theater is what makes "goes dark after they
 * watch half" actually true in here.
 *
 * The only work left is the shape. Module drills come straight off the
 * drills table (`mux_playback_id`), and the theater speaks the content item
 * shape (`muxPlaybackId`).
 *
 * And it renders into document.body, not in place. The reader's page
 * transition animates `transform` with a fill, and Chrome treats a filled
 * transform animation as a containing block for fixed descendants. In place,
 * the "full screen" theater was sized to the reading column instead of the
 * window and centred halfway down it, so on a long page (the journey layout)
 * the video opened thousands of pixels below the screen. A portal puts it
 * back where `position: fixed` means the window.
 *
 * No em dashes.
 */
import React from "react";
import { createPortal } from "react-dom";
import { VideoTheater } from "../content/VideoTheater";
import { useWatched } from "../hooks/useWatched";

export function DrillTheater({ drill, C, dark, onClose }) {
  const { watched, markWatched, toggleWatched } = useWatched();
  if (!drill) return null;

  const item = {
    ...drill,
    muxPlaybackId: drill.muxPlaybackId || drill.mux_playback_id,
  };

  return createPortal(
    <VideoTheater
      item={item}
      C={C}
      dark={dark}
      onClose={onClose}
      onWatchedThreshold={(_it, pct) => markWatched(drill.id, pct)}
      watched={watched.has(drill.id)}
      onToggleWatched={() => toggleWatched(drill.id)}
    />,
    document.body
  );
}

export default DrillTheater;
